import config from '@/config';
import { SyncGoogleCourses } from '@/types/register';
import {
  LanguageSettingsResponse,
  LevelSettingsResponse,
  SetUserLanguageSettingsResponse,
  UpdatedStudentSettingsResponse,
  UpdatedTeacherClassResponse,
  ContactInfoResponse,
  DeleteStudentResponse,
  DeleteClassResponse,
  GetGoogleCoursesResponse,
  SetGoogleCoursesResponse,
  ResetPasswordByTeacherResponse,
  RequestPasswordChangeResponse,
  TeacherGroupsResponse,
} from '@/types/response';
import { UserService } from '@/types/services';
import { UserState } from '@/types/user';
import axiosInstance from '@/utils/api-new';
import UserUtils from '@/utils/user';

export const userService: UserService = {
  fetchLevelSettings: async user => {
    const resp: LevelSettingsResponse | null = await axiosInstance.get(
      `${config.apiVersion}/${UserUtils.fromToLanguage(user)}/settings/levels`
    );

    return resp;
  },
  fetchLanguageSettings: async user => {
    const resp: LanguageSettingsResponse | null = await axiosInstance.get(
      `${config.apiVersion}/${UserUtils.fromToLanguage(user)}/settings/languages`
    );

    return resp;
  },
  setUserLanguageSettings: async (user, interfaceLanguage: string) => {
    const resp: SetUserLanguageSettingsResponse | null = await axiosInstance.post(
      `${config.apiVersion}/user-settings/set-user-interface-language/${user?.metadata.id}`,
      {
        interfaceLanguageCode: interfaceLanguage,
      }
    );

    return resp;
  },
  setUserLogOut: async user => {
    await axiosInstance.post(`${config.apiVersion}/logout/${user?.metadata.id}`);
  },
  updateUserAvatar: async (user: UserState | null, avatarName: string, avatarColor: string) => {
    const resp: UpdatedStudentSettingsResponse | null = await axiosInstance.post(
      `${config.apiVersion}/user-settings/update-user-avatar/${user?.metadata.id}`,
      {
        avatarName,
        avatarColor,
      }
    );

    return resp;
  },
  updateStudentSettings: async (
    user: UserState | null,
    studentId: string,
    firstName: string,
    lastName: string,
    interfaceLanguage: string
  ) => {
    const resp: UpdatedStudentSettingsResponse | null = await axiosInstance.post(
      `${config.apiVersion}/teacher-view/teacher-update-user-settings/${user?.metadata.id}/${studentId}`,
      {
        firstName,
        lastName,
        interfaceLanguage: {
          interfaceLanguageCode: interfaceLanguage,
        },
      }
    );

    return resp;
  },

  updateStudentLevel: async (user: UserState | null, studentId: string, level: string) => {
    const resp: UpdatedStudentSettingsResponse | null = await axiosInstance.put(
      `${config.apiVersion}/teacher-view/update-student-level/${user?.metadata.id}/${studentId}/${level}`
    );

    return resp;
  },

  editGroup: async (user, groupId, groupDetails) => {
    const resp: UpdatedTeacherClassResponse | null = await axiosInstance.post(
      `${config.apiVersion}/Teacher-view/update-group/${user?.metadata?.id}/${groupId}`,
      {
        groupName: groupDetails.groupName,
        groupGrade: groupDetails.grade,
      }
    );

    return resp;
  },

  sendContactInfo: async (user, info) => {
    const resp: ContactInfoResponse | null = await axiosInstance.post(
      `${config.apiVersion}/teacher-view/teacher-contact-us/${user?.metadata?.id}`,
      info
    );

    return resp;
  },

  fetchCanDoData: async (user: UserState | null) => {
    const resp: any | null = await axiosInstance.get(
      `${config.apiVersion}/${UserUtils.fromToLanguage(user)}/settings/can-do-answers`
    );

    return resp;
  },

  deleteStudent: async (user: UserState, studentId: string, reason: string) => {
    const resp: DeleteStudentResponse | null = await axiosInstance.post(
      `${config.apiVersion}/teacher-view/delete-user/${user?.metadata?.id}/${studentId}`,
      {
        reason: reason,
      }
    );

    return resp;
  },

  deleteClass: async (user: UserState, classId: string, reason: string) => {
    const schoolId = user?.metadata?.organizationDetails?.organizationId;
    if (!schoolId) {
      return null;
    }

    const url = `/admin-management/${config.apiVersion}/${schoolId}/groups/${classId}`;
    const deleteConfig = reason.trim() ? { data: { reason: reason.trim() } } : undefined;

    const resp: DeleteClassResponse | null = await axiosInstance.delete(url, deleteConfig);

    return resp;
  },

  getGoogleCourses: async (user: UserState | null, action: string) => {
    const resp: GetGoogleCoursesResponse | null = await axiosInstance.get(
      `${config.apiVersion}/google-classroom/courses/${user?.metadata?.id}?action=${action}`
    );

    return resp;
  },

  setGoogleCourses: async (user: UserState | null, syncCourses: SyncGoogleCourses[]) => {
    const resp: SetGoogleCoursesResponse | null = await axiosInstance.post(
      `${config.apiVersion}/google-classroom/sync-data/${user?.metadata?.id}`,
      { syncCourses }
    );

    return resp;
  },

  resetPasswordByTeacher: async (
    user: UserState | null,
    strudentId: string,
    studentUsername: string
  ) => {
    const resp: ResetPasswordByTeacherResponse | null = await axiosInstance.post(
      `${config.apiVersion}/teacher-view/reset-password/${user?.metadata?.id}/${strudentId}/${studentUsername}`
    );

    return resp;
  },

  requestPasswordChange: async (userName: string) => {
    const resp: RequestPasswordChangeResponse | null = await axiosInstance.post(
      `${config.apiVersion}/user/forgot-password/`,
      {
        userName,
      }
    );

    return resp;
  },

  fetchTeacherGroups: async (organizationId: string, teacherId: string) => {
    const resp: TeacherGroupsResponse | null = await axiosInstance.get(
      `${config.apiVersion}/teacher-view/teacher-groups/${organizationId}/${teacherId}`
    );
    return resp;
  },
};
